import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
// Button removed - causes production crash
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Upload, Sparkles, Calendar, TrendingUp, Zap, CheckCircle, ArrowRight, Play, Users, Target, Award, Star, Instagram, Facebook, Twitter, Video, Heart, MessageCircle, Share2, Camera, Info, ShoppingCart, UserCheck, DollarSign, ChefHat, Building2, BarChart3, Menu, X, Globe } from 'lucide-react';
import CurrencySelector from '../components/CurrencySelector';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useCurrency } from '../contexts/CurrencyContext';
import PlanComparisonModal from '../components/PlanComparisonModal';
import AuthModal from '../components/AuthModal';
import getBackendUrl from '../utils/backendUrl';

const AUTH_BASE_URL = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
const BACKEND_URL = getBackendUrl();

// FlavorFeed Hero Video URL (Full-width hero)
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_kitchen-print-fix/artifacts/wa0sfhc6_grok-video-282cd139-3ba3-4063-bcd2-fb8fc1fddccb%20%281%29.mp4";

// FlavorFeed Promotional Video URL (Secondary section)
const PROMO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_multistationpos/artifacts/qsej6dhr_grok-video-9cb30fa8-3fe4-420e-bb2e-149d1f1674c7%20%281%29.mp4";

// Helper function to get auth URL dynamically
const getAuthURL = () => {
  const redirectURL = `${window.location.origin}/dashboard`;
  console.log('🔗 Auth redirect URL:', redirectURL);
  return `${AUTH_BASE_URL}/?redirect=${encodeURIComponent(redirectURL)}`;
};

export default function Landing({ user }) {
  const { t, i18n } = useSafeTranslation();
  const { formatPrice, currency } = useCurrency();
  const [scrollY, setScrollY] = useState(0);
  const [activeMetric, setActiveMetric] = useState(0);
  const [showComparison, setShowComparison] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // 'monthly' or 'annual'
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isVideoInView, setIsVideoInView] = useState(false);
  const [isHeroVideoInView, setIsHeroVideoInView] = useState(true);
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);
  const heroVideoRef = useRef(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const isRTL = i18n.language === 'ar';

  // Detect first user interaction to enable sound
  useEffect(() => {
    const enableSound = () => {
      setHasUserInteracted(true);
      const heroVideo = heroVideoRef.current;
      if (heroVideo && isHeroVideoInView) {
        heroVideo.muted = false;
        heroVideo.volume = 0.3;
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', enableSound);
      document.removeEventListener('scroll', enableSound);
      document.removeEventListener('touchstart', enableSound);
    };

    document.addEventListener('click', enableSound);
    document.addEventListener('scroll', enableSound);
    document.addEventListener('touchstart', enableSound);

    return () => {
      document.removeEventListener('click', enableSound);
      document.removeEventListener('scroll', enableSound);
      document.removeEventListener('touchstart', enableSound);
    };
  }, [isHeroVideoInView]);

  // Start video playback on mount (muted initially for autoplay policy)
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }
  }, []);

  // Hero Video: Play with sound when visible (after user interaction), mute when scrolled away
  useEffect(() => {
    const heroVideo = heroVideoRef.current;
    if (!heroVideo) return;

    // Start playing muted
    heroVideo.muted = true;
    heroVideo.play().catch(() => {});

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video is in view - unmute if user has interacted
            setIsHeroVideoInView(true);
            if (hasUserInteracted) {
              heroVideo.muted = false;
              heroVideo.volume = 0.3;
            }
            heroVideo.play().catch(() => {});
          } else {
            // Video is out of view - MUTE
            setIsHeroVideoInView(false);
            heroVideo.muted = true;
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '-50px'
      }
    );

    observer.observe(heroVideo);

    return () => {
      observer.disconnect();
    };
  }, [hasUserInteracted]);

  // Handle sound based on scroll visibility for secondary video
  useEffect(() => {
    const videoSection = videoSectionRef.current;
    const video = videoRef.current;
    
    if (!videoSection || !video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Video is in view - unmute (sound ON)
            setIsVideoInView(true);
            video.muted = false;
            video.volume = 0.5; // 50% volume when in view
          } else {
            // Video is out of view - mute (sound OFF) but keep playing
            setIsVideoInView(false);
            video.muted = true;
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of video is visible
        rootMargin: '0px'
      }
    );

    observer.observe(videoSection);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveMetric((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Handle post-login checkout redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutPlan = urlParams.get('checkout');
    const checkoutBilling = urlParams.get('billing');
    
    if (checkoutPlan && checkoutBilling && user) {
      // User just logged in and needs to checkout
      const initiateCheckout = async () => {
        try {
          // Get stored currency (default to USD if not set)
          const storedCurrency = sessionStorage.getItem('intended_currency') || 'USD';
          
          const response = await axios.post(
            `${BACKEND_URL}/api/subscriptions/checkout`,
            {
              plan: checkoutPlan,
              billing_period: checkoutBilling,
              currency: storedCurrency
            },
            { withCredentials: true }
          );
          
          if (response.data.checkout_url) {
            // Clear stored values and redirect to Stripe
            sessionStorage.removeItem('intended_plan');
            sessionStorage.removeItem('intended_billing');
            sessionStorage.removeItem('intended_currency');
            window.history.replaceState({}, '', '/');
            window.location.href = response.data.checkout_url;
          } else {
            alert('Unable to process checkout. Please try selecting your plan again.');
          }
        } catch (error) {
          console.error('Auto-checkout error:', error);
          alert('Unable to process checkout. Please try selecting your plan again.');
          window.history.replaceState({}, '', '/');
        }
      };
      
      initiateCheckout();
    }
  }, [user]);

  const metrics = [
    { value: '0', label: 'Contracts', icon: <Award className="w-6 h-6" /> },
    { value: '9', label: 'Modules', icon: <ShoppingCart className="w-6 h-6" /> },
    { value: '$29', label: 'Cheapest Paid', icon: <DollarSign className="w-6 h-6" /> },
    { value: '$0', label: 'Free Forever Plan', icon: <Award className="w-6 h-6" /> }
  ];

  

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      <Helmet>
        <title>FlavorFeed AI — Free Restaurant POS for US & Canada · Toast, Square & Clover Alternative</title>
        <meta name="description" content="A truly-free restaurant POS — built for US, Canada, UK, Europe & MENA. Free forever. No contracts. No hardware lock-in. Toast / Square / Clover alternative with AI marketing, KDS, digital signage, online ordering, and Apple Pay. Launch in 5 minutes." />
        <link rel="canonical" href="https://www.flavorfeed.ai/" />
        <meta property="og:title" content="FlavorFeed AI — Free Restaurant POS · Toast / Square / Clover Alternative" />
        <meta property="og:description" content="Free forever restaurant POS for US, Canada, UK, EU & MENA. AI marketing, KDS, digital signage, online ordering, Apple Pay, Stripe Reader. No contracts. Launch in 5 min." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.flavorfeed.ai/" />
        <meta property="og:image" content="https://www.flavorfeed.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="FlavorFeed — Free Restaurant POS for US & Canada" />
        <meta property="og:site_name" content="FlavorFeed AI" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="en_CA" />
        <meta property="og:locale:alternate" content="en_GB" />
        <meta property="og:locale:alternate" content="ar_SA" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.flavorfeed.ai/" />
        <meta name="twitter:title" content="FlavorFeed AI — Free Restaurant POS (US, Canada, UK)" />
        <meta name="twitter:description" content="Free forever cloud POS · AI marketing · KDS · digital signage · Apple Pay · Stripe. Toast/Square/Clover alternative. No contract." />
        <meta name="twitter:image" content="https://www.flavorfeed.ai/twitter-card.png" />
      </Helmet>
      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-lg border-b border-gray-800">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-8">
              <img 
                src="/flavorfeed-logo.png" 
                alt="FlavorFeed AI" 
                className="h-14 w-auto cursor-pointer"
                onClick={() => window.location.href = '/'}
              />
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-6">
                <a href="/demo" className="text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  AI Demo
                </a>
                <a href="#features" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">{t('landing.features')}</a>
                <a href="#demos" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">{t('landing.demo')}</a>
                <a href="/login" className="text-gray-300 hover:text-white transition-colors text-sm font-medium flex items-center gap-1">
                  <ShoppingCart className="w-4 h-4" />
                  {t('landing.posSystem')}
                </a>
                <a href="#pricing-section" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">{t('landing.pricing')}</a>
                <a href="#faq" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">FAQ</a>
                <Link to="/blog" className="text-gray-300 hover:text-white transition-colors text-sm font-medium">Blog</Link>
              </div>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:block">
                <CurrencySelector variant="ghost" showLabel={false} />
              </div>
              <LanguageSwitcher variant="ghost" showLabel={false} />
              <button
                onClick={() => user ? window.location.href = '/dashboard' : window.location.href = '/login'}
                className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white px-6 py-2 text-sm rounded-full"
              >
                {user ? t('landing.dashboard') : t('landing.getStarted')}
              </button>
              
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden text-white"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
              >
                {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-800 pt-4">
              <div className="flex flex-col gap-3">
                <a href="/demo" className="text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium py-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  AI Demo
                </a>
                <a href="#features" className="text-gray-300 hover:text-white transition-colors text-sm font-medium py-2">{t('landing.features')}</a>
                <a href="#demos" className="text-gray-300 hover:text-white transition-colors text-sm font-medium py-2">{t('landing.demo')}</a>
                <a href="/login" className="text-gray-300 hover:text-white transition-colors text-sm font-medium py-2 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  {t('landing.posSystem')}
                </a>
                <a href="#pricing-section" className="text-gray-300 hover:text-white transition-colors text-sm font-medium py-2">{t('landing.pricing')}</a>
                <a href="#faq" className="text-gray-300 hover:text-white transition-colors text-sm font-medium py-2">FAQ</a>
                <Link to="/blog" className="text-gray-300 hover:text-white transition-colors text-sm font-medium py-2">Blog</Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse" style={{ transform: `translateY(${scrollY * 0.5}px)` }}></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s', transform: `translateY(${scrollY * -0.3}px)` }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-coral-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s', transform: `translateY(${scrollY * 0.4}px)` }}></div>
      </div>

      {/* Full-Width Video Hero Section */}
      <section className="relative pt-20 bg-gray-900">
        <div className="relative w-full min-h-[600px] overflow-visible flex items-center justify-center px-6 sm:px-8 py-8">
          {/* Background glow effects */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>
          
          {/* Video Frame Container - Full frame visible */}
          <div className="relative w-full max-w-5xl mx-auto">
            {/* Glowing border effect - all sides */}
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-600 via-teal-500 to-purple-600 rounded-3xl blur-md opacity-60 animate-pulse"></div>
            
            {/* Video Container with full frame */}
            <div className="relative bg-gray-900 rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl shadow-purple-500/30">
              {/* Video Player - object-contain to show full video */}
              <video
                ref={heroVideoRef}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-label="FlavorFeed AI restaurant POS demo video"
                className="w-full aspect-video object-contain bg-black"
              >
                <source src={HERO_VIDEO_URL} type="video/mp4" />
              </video>
              
              {/* Frame border overlay - top */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-teal-400 to-purple-500"></div>
              {/* Frame border overlay - bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-teal-400 to-purple-500"></div>
              {/* Frame border overlay - left */}
              <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-b from-purple-500 via-teal-400 to-purple-500"></div>
              {/* Frame border overlay - right */}
              <div className="absolute top-0 bottom-0 right-0 w-1 bg-gradient-to-b from-purple-500 via-teal-400 to-purple-500"></div>
              
              {/* Sound Indicator - Click to toggle sound */}
              <button 
                onClick={() => {
                  const heroVideo = heroVideoRef.current;
                  if (heroVideo) {
                    if (heroVideo.muted) {
                      heroVideo.muted = false;
                      heroVideo.volume = 0.3;
                      setHasUserInteracted(true);
                    } else {
                      heroVideo.muted = true;
                    }
                    setIsHeroVideoInView(!heroVideo.muted);
                  }
                }}
                className={`absolute top-4 right-4 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm flex items-center gap-2 transition-all duration-300 z-20 cursor-pointer hover:bg-black/90 ${isHeroVideoInView ? 'opacity-100' : 'opacity-80'}`}
              >
                {isHeroVideoInView && hasUserInteracted ? (
                  <>
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span>🔊 Sound On</span>
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    <span>🔇 Click for Sound</span>
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Scroll Indicator */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
            <div className="w-6 h-10 border-2 border-purple-400/50 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-3 bg-purple-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section - Moved from old Hero */}
      <section className="relative py-24 bg-gradient-to-b from-gray-900 via-purple-900/30 to-gray-900 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/15 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            {/* Logo at top of section */}
            <div className="mb-8 animate-fade-in">
              <img 
                src="/flavorfeed-logo.png" 
                alt="FlavorFeed AI" 
                className="h-20 sm:h-24 lg:h-28 w-auto mx-auto drop-shadow-2xl"
              />
            </div>
            
            {/* Animated Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600/20 to-teal-600/20 backdrop-blur-xl border border-purple-500/30 px-6 py-3 rounded-full mb-8 animate-slide-up" dir={isRTL ? 'rtl' : 'ltr'}>
              <Zap className="w-5 h-5 text-teal-400 animate-pulse" />
              <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-teal-400">{t('landing.badge')}</span>
            </div>
            
            {/* Dynamic Typography — Semantic H1 for SEO */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight text-center" dir={isRTL ? 'rtl' : 'ltr'}>
              <span className="block text-white" style={{ fontFamily: isRTL ? 'Almarai, sans-serif' : 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
                {t('landing.heroTitle1')}
              </span>
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-teal-400 to-coral-400 animate-gradient" style={{ fontFamily: isRTL ? 'Almarai, sans-serif' : 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
                {t('landing.heroTitle2')}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed text-center" style={{ fontFamily: isRTL ? 'Almarai, sans-serif' : 'Inter, sans-serif' }} dir={isRTL ? 'rtl' : 'ltr'}>
              {t('landing.heroSubtitle')}
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <button
                data-testid="hero-get-started-btn"
                onClick={() => user ? window.location.href = '/dashboard' : window.location.href = '/login'}
                className="group relative bg-gradient-to-r from-purple-600 via-purple-500 to-teal-500 hover:from-purple-700 hover:via-purple-600 hover:to-teal-600 text-white px-10 py-5 text-lg font-semibold rounded-full shadow-2xl hover:shadow-purple-500/50 transform hover:scale-105 transition-all duration-300 overflow-hidden flex items-center justify-center gap-2"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {user ? t('landing.goToDashboard') : t('landing.startFree')}
                  <ArrowRight className={`w-5 h-5 group-hover:translate-x-2 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-coral-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </button>
              
              <a
                href="/demo"
                data-testid="hero-ai-demo-btn"
                className="group border-2 border-purple-400/50 bg-purple-900/30 backdrop-blur-xl text-purple-300 hover:bg-purple-500/20 hover:border-purple-400 px-10 py-5 text-lg font-semibold rounded-full transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform" />
                Try AI Demo
              </a>
              
              <button
                data-testid="hero-demo-btn"
                className="group border-2 border-teal-400/50 bg-gray-900/50 backdrop-blur-xl text-teal-300 hover:bg-teal-500/20 hover:border-teal-400 px-10 py-5 text-lg font-semibold rounded-full transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                onClick={() => setShowVideoModal(true)}
              >
                <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                {t('landing.watchDemo')}
              </button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400 mb-10" dir={isRTL ? 'rtl' : 'ltr'}>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-teal-400" />
                {t('landing.noCredit')}
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-purple-400" />
                {t('landing.freePlan')}
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-coral-400" />
                {t('landing.freeImages')}
              </div>
            </div>

            {/* Feature Pills - Module-Focused */}
            <div>
              <p className="text-xs text-gray-500 mb-4 font-semibold uppercase tracking-wider" dir={isRTL ? 'rtl' : 'ltr'}>MODULES</p>
              <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
                <Badge className="bg-gradient-to-r from-purple-600/80 to-purple-500/80 text-white px-4 py-2 text-sm font-semibold hover:scale-105 transition-transform cursor-pointer">
                  <ShoppingCart className={`w-4 h-4 ${isRTL ? 'ml-1.5' : 'mr-1.5'} inline`} />
                  POS & Orders
                </Badge>
                <Badge className="bg-gradient-to-r from-indigo-600/80 to-purple-500/80 text-white px-4 py-2 text-sm font-semibold hover:scale-105 transition-transform cursor-pointer">
                  <ChefHat className={`w-4 h-4 ${isRTL ? 'ml-1.5' : 'mr-1.5'} inline`} />
                  Kitchen Display
                </Badge>
                <Badge className="bg-gradient-to-r from-pink-600/80 to-rose-500/80 text-white px-4 py-2 text-sm font-semibold hover:scale-105 transition-transform cursor-pointer">
                  <Sparkles className={`w-4 h-4 ${isRTL ? 'ml-1.5' : 'mr-1.5'} inline`} />
                  AI Content Studio
                </Badge>
                <Badge className="bg-gradient-to-r from-teal-600/80 to-cyan-500/80 text-white px-4 py-2 text-sm font-semibold hover:scale-105 transition-transform cursor-pointer">
                  <BarChart3 className={`w-4 h-4 ${isRTL ? 'ml-1.5' : 'mr-1.5'} inline`} />
                  Inventory & Analytics
                </Badge>
              </div>
            </div>
          </div>
          
          {/* Module Highlights Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto">
            {[
              { icon: <ShoppingCart className="w-6 h-6" />, label: 'Point of Sale', sub: 'Free or $29' },
              { icon: <ChefHat className="w-6 h-6" />, label: 'Kitchen Display', sub: 'From $29' },
              { icon: <Sparkles className="w-6 h-6" />, label: 'AI Marketing', sub: '$39/mo' },
              { icon: <Building2 className="w-6 h-6" />, label: 'Multi-Location', sub: 'Enterprise' },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-purple-500/40 hover:bg-white/10 transition-all duration-300 group cursor-pointer">
                <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-gradient-to-br from-purple-600/30 to-teal-500/30 flex items-center justify-center text-purple-400 group-hover:scale-110 group-hover:text-teal-400 transition-all">
                  {item.icon}
                </div>
                <span className="text-gray-300 text-sm font-semibold block">{item.label}</span>
                <span className="text-gray-500 text-xs">{item.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Metrics Section - Animated */}
      <section className="relative py-24 bg-gradient-to-b from-gray-900 to-black border-y border-purple-500/20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {metrics.map((metric, index) => (
              <div
                key={index}
                className={`text-center group cursor-pointer transition-all duration-500 ${
                  activeMetric === index ? 'scale-110' : 'scale-100 opacity-70'
                }`}
                onClick={() => setActiveMetric(index)}
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-600 to-teal-500 flex items-center justify-center text-white transform group-hover:rotate-12 transition-transform">
                  {metric.icon}
                </div>
                <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-teal-400 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {metric.value}
                </div>
                <div className="text-gray-400 font-medium">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Module Showcase */}
      <section id="features" className="relative py-32 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <Badge className="mb-6 bg-gradient-to-r from-purple-600 to-teal-600 text-white px-6 py-2 text-base">MODULES</Badge>
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-teal-400 to-coral-400 animate-gradient">
                Everything You Need
              </span>
              <br />One Platform
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">Base POS + add only the modules you need. Scale as your restaurant grows.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <ShoppingCart />, title: 'POS Terminal', desc: 'Take orders, manage tables, print receipts & KOT. Dine-in, takeaway, delivery — all in one.', color: 'purple', badge: 'FREE', price: 'Included in Free' },
              { icon: <ChefHat />, title: 'Kitchen Display (KDS)', desc: 'Real-time order queue for your kitchen. Auto-prioritize, mark ready, and notify front-of-house.', color: 'teal', badge: 'CORE', price: 'Included in $29' },
              { icon: <TrendingUp />, title: 'Inventory & Recipes', desc: 'Track stock levels, manage recipes, auto-deduct ingredients on sale. AI invoice scanner adds auto-stock-in.', color: 'teal', badge: 'STARTER', price: 'In $79' },
              { icon: <Sparkles />, title: 'AI Content Studio', desc: '9 styles — Instagram posts, reels, TikToks, stories. Auto-caption, auto-hashtag, auto-schedule.', color: 'purple', badge: 'ADD-ON', price: '$39/mo' },
              { icon: <Target />, title: 'Digital Signage', desc: 'TV menu boards with 5 themes, multi-screen rotation, welcome & promo screens. L/P pairing.', color: 'coral', badge: 'ADD-ON', price: '$19/mo' },
              { icon: <Heart />, title: 'Loyalty Program', desc: 'Points, tiers, VIP perks, SMS & email campaigns. Build repeat customers.', color: 'teal', badge: 'ADD-ON', price: '$14/mo' },
              { icon: <Globe />, title: 'Online Ordering', desc: 'Branded ordering portal + QR-code table ordering. Direct to your kitchen, no DoorDash markup.', color: 'coral', badge: 'ADD-ON', price: '$19/mo' },
              { icon: <Users />, title: 'Multi-Branch HQ', desc: 'Control tower for chains: branch-specific menus, consolidated reports, central warehouse transfers.', color: 'purple', badge: 'ENTERPRISE', price: 'In $249' }
            ].map((feature, index) => (
              <div
                key={index}
                className="group relative p-8 rounded-3xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 hover:border-${feature.color}-500/50 backdrop-blur-sm overflow-hidden transition-all duration-500 hover:scale-105 hover:-translate-y-2"
                style={{ animationDelay: `${index * 0.1}s` }}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                {/* Glow Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br from-${feature.color}-600/0 to-${feature.color}-600/0 group-hover:from-${feature.color}-600/20 group-hover:to-${feature.color}-600/5 transition-all duration-500`}></div>
                
                <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br from-${feature.color}-600 to-${feature.color}-400 flex items-center justify-center text-white mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                  {feature.icon}
                </div>
                <h3 className="relative text-2xl font-bold mb-3 text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-${feature.color}-400 group-hover:to-${feature.color}-200 transition-all" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {feature.title}
                  {feature.badge && (
                    <Badge className={`${isRTL ? 'mr-2' : 'ml-2'} ${
                      feature.badge === 'FREE' ? 'bg-emerald-500' :
                      feature.badge === 'CORE' ? 'bg-cyan-500' :
                      feature.badge === 'STARTER' ? 'bg-gray-500' :
                      feature.badge === 'ENTERPRISE' ? 'bg-amber-500' :
                      feature.badge === 'BASE' ? 'bg-green-500' :
                      feature.badge === 'PRO' ? 'bg-purple-500' :
                      'bg-teal-500'
                    } text-white text-xs`}>{feature.badge}</Badge>
                  )}
                </h3>
                <p className="relative text-gray-400 group-hover:text-gray-300 transition-colors mb-3">
                  {feature.desc}
                </p>
                {feature.price && (
                  <p className="relative text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-teal-400">{feature.price}</p>
                )}
                
                {/* Hover Arrow */}
                <ArrowRight className={`absolute bottom-6 ${isRTL ? 'left-6 rotate-180' : 'right-6'} w-6 h-6 text-${feature.color}-400 opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all duration-500`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Demo Section - Interactive */}
      <section id="demos" className="relative py-32 bg-gradient-to-b from-gray-900 via-black to-gray-900 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <Badge className="mb-6 bg-gradient-to-r from-purple-600 to-teal-600 text-white px-6 py-2 text-base">LIVE AI DEMO</Badge>
            <h2 className="text-5xl sm:text-6xl font-black mb-6 text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }} dir={isRTL ? 'rtl' : 'ltr'}>
              {t('landing.demoTitle')}
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-teal-400">
                {t('landing.demoSubtitle')}
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
              {t('landing.demoDesc')}
            </p>
          </div>

          {/* Interactive Demo Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            
            {/* Demo 1: AI Caption Generator */}
            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-teal-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-gray-800/90 backdrop-blur-xl rounded-3xl p-8 border border-purple-500/30 hover:border-purple-500/60 transition-all duration-500 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-teal-600 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-black text-white">AI Caption Generator</h3>
                </div>
                
                {/* Mock Food Image */}
                <div className="relative rounded-xl overflow-hidden mb-4 h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&h=400&fit=crop&auto=format&q=75" 
                    alt="Pizza"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                    AI Analyzing...
                  </div>
                </div>
                
                {/* Generated Caption */}
                <div className="bg-gray-900/80 rounded-xl p-4 space-y-2 flex-grow">
                  <div className="flex items-start gap-2">
                    <MessageCircle className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-white font-semibold text-sm mb-1">Generated:</p>
                      <p className="text-gray-300 text-xs leading-relaxed line-clamp-3">
                        "Freshly baked perfection! 🍕 Our wood-fired margherita pizza with San Marzano tomatoes, creamy mozzarella, and fresh basil."
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {['#Pizza', '#Foodie'].map((tag) => (
                      <span key={tag} className="text-xs bg-purple-600/30 text-purple-300 px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="mt-auto pt-3 flex items-center gap-2 text-purple-400 text-xs">
                  <CheckCircle className="w-3 h-3" />
                  <span>Generated in seconds</span>
                </div>
              </div>
            </div>

            {/* Demo 2: AI Image Generator */}
            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-r from-coral-600/20 to-purple-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-gray-800/90 backdrop-blur-xl rounded-3xl p-8 border border-coral-500/30 hover:border-coral-500/60 transition-all duration-500 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-coral-600 to-purple-600 flex items-center justify-center">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-black text-white">AI Image Creator</h3>
                </div>
                
                {/* Prompt Input */}
                <div className="bg-gray-900/80 rounded-xl p-3 mb-3">
                  <p className="text-xs text-gray-400 mb-1">Your Prompt:</p>
                  <p className="text-white font-medium text-sm">"Chocolate lava cake"</p>
                </div>
                
                {/* Generated Image */}
                <div className="relative rounded-xl overflow-hidden mb-4 h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1624353365286-3f8d62daad51?w=600&h=400&fit=crop&auto=format&q=75" 
                    alt="Chocolate Lava Cake"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 bg-coral-600 text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                    AI Generated ✨
                  </div>
                </div>
                
                {/* Options */}
                <div className="flex flex-wrap gap-2 mb-3 flex-grow">
                  <button className="bg-coral-600/30 hover:bg-coral-600/50 text-coral-300 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all">
                    🎨 Style
                  </button>
                  <button className="bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all">
                    🔄 Regen
                  </button>
                </div>
                
                <div className="mt-auto flex items-center gap-2 text-coral-400 text-xs">
                  <CheckCircle className="w-3 h-3" />
                  <span>AI Generated • High Quality</span>
                </div>
              </div>
            </div>

            {/* Demo 3: AI Video Script */}
            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-r from-teal-600/20 to-blue-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-gray-800/90 backdrop-blur-xl rounded-3xl p-8 border border-teal-500/30 hover:border-teal-500/60 transition-all duration-500 h-full flex flex-col">
                {/* Future Feature Badge */}
                <div className="absolute -top-3 -right-3 z-10">
                  <Badge className="bg-gradient-to-r from-purple-600 to-red-600 text-white px-4 py-1.5 text-xs font-bold shadow-lg animate-pulse">
                    🚀 COMING SOON
                  </Badge>
                </div>
                
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-600 to-blue-600 flex items-center justify-center">
                    <Video className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-black text-white">AI Video Script</h3>
                </div>
                
                {/* Video Preview */}
                <div className="relative rounded-xl overflow-hidden mb-4 h-48 bg-gradient-to-br from-teal-900/50 to-blue-900/50 flex items-center justify-center">
                  <div className="text-center">
                    <Play className="w-16 h-16 text-teal-400 mb-3 mx-auto animate-pulse" />
                    <p className="text-white font-bold text-sm">3 Photos → Viral Reel</p>
                  </div>
                </div>
                
                {/* Script Preview */}
                <div className="bg-gray-900/80 rounded-xl p-4 space-y-2 flex-grow">
                  <div className="text-xs text-gray-400 mb-2">AI Script:</div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex gap-2">
                      <span className="text-teal-400 font-bold">Scene 1:</span>
                      <span className="text-gray-300">Sizzle shot</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-teal-400 font-bold">Scene 2:</span>
                      <span className="text-gray-300">Plating</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-teal-400 font-bold">Scene 3:</span>
                      <span className="text-gray-300">Final reveal</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-700">
                    <span className="text-xs text-gray-500">🎵 Trending Music</span>
                  </div>
                </div>
                
                <div className="mt-auto pt-3 flex items-center gap-2 text-purple-400 text-xs">
                  <Zap className="w-3 h-3" />
                  <span>Future Feature</span>
                </div>
              </div>
            </div>

            {/* Demo 4: Multi-Language Translation */}
            <div className="relative group h-full">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
              <div className="relative bg-gray-800/90 backdrop-blur-xl rounded-3xl p-8 border border-pink-500/30 hover:border-pink-500/60 transition-all duration-500 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-600 to-purple-600 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-black text-white">AI Translation</h3>
                </div>
                
                {/* Original Caption */}
                <div className="bg-gray-900/80 rounded-xl p-3 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs bg-blue-600/30 text-blue-300 px-2 py-0.5 rounded">🇬🇧 EN</span>
                  </div>
                  <p className="text-white text-xs">
                    "Our signature sushi! 🍣"
                  </p>
                </div>
                
                {/* Translations */}
                <div className="space-y-2 flex-grow">
                  <div className="bg-purple-900/20 rounded-lg p-2.5 border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-pink-600/30 text-pink-300 px-2 py-0.5 rounded">🇪🇸 ES</span>
                    </div>
                    <p className="text-gray-300 text-xs">
                      "¡Nuestro sushi exclusivo! 🍣"
                    </p>
                  </div>
                  
                  <div className="bg-purple-900/20 rounded-lg p-2.5 border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-pink-600/30 text-pink-300 px-2 py-0.5 rounded">🇫🇷 FR</span>
                    </div>
                    <p className="text-gray-300 text-xs">
                      "Notre sushi signature! 🍣"
                    </p>
                  </div>
                  
                  <div className="bg-purple-900/20 rounded-lg p-2.5 border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-pink-600/30 text-pink-300 px-2 py-0.5 rounded">🇦🇪 AR</span>
                    </div>
                    <p className="text-gray-300 text-xs" dir="rtl">
                      "سوشي المميز! 🍣"
                    </p>
                  </div>
                </div>
                
                <div className="mt-auto pt-3 flex items-center gap-2 text-pink-400 text-xs">
                  <CheckCircle className="w-3 h-3" />
                  <span>12 languages supported</span>
                </div>
              </div>
            </div>

          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <button
              onClick={() => user ? window.location.href = '/dashboard' : window.location.href = '/login'}
              className="bg-gradient-to-r from-purple-600 via-teal-600 to-coral-600 hover:from-purple-700 hover:via-teal-700 hover:to-coral-700 text-white px-12 py-6 text-xl rounded-full shadow-2xl hover:shadow-purple-500/50 transform hover:scale-105 transition-all duration-300 font-black"
            >
              {user ? t('landing.goToDashboard') : t('landing.tryFree')}
              <ArrowRight className={`inline-block w-6 h-6 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
            </button>
          </div>
        </div>
      </section>

      {/* AI vs Traditional POS Comparison Section */}
      <section className="relative py-32 bg-gradient-to-b from-gray-900 via-black to-gray-900 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-6 relative">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 text-base">
              {isRTL ? 'لماذا نحن مختلفون' : 'WHY WE\'RE DIFFERENT'}
            </Badge>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">FlavorFeed AI</span>
              <span className="text-gray-500"> vs </span>
              <span className="text-gray-400">{isRTL ? 'أنظمة نقاط البيع التقليدية' : 'Traditional POS'}</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {isRTL 
                ? 'اكتشف لماذا تتحول المطاعم من الأنظمة التقليدية إلى الذكاء الاصطناعي'
                : 'Discover why restaurants are switching from traditional systems to AI-powered solutions'}
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="bg-gray-800/50 border border-gray-700 rounded-3xl overflow-hidden backdrop-blur-sm">
              {/* Header */}
              <div className="grid grid-cols-3 bg-gray-800/80 border-b border-gray-700">
                <div className="p-6 text-gray-400 font-semibold">{isRTL ? 'الميزة' : 'Feature'}</div>
                <div className="p-6 text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-full">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    <span className="font-bold text-purple-300">FlavorFeed AI</span>
                  </div>
                </div>
                <div className="p-6 text-center text-gray-500 font-semibold">{isRTL ? 'النظام التقليدي' : 'Traditional POS'}</div>
              </div>

              {/* Comparison Rows */}
              {[
                { feature: isRTL ? 'توليد صور الطعام بالذكاء الاصطناعي' : 'AI Menu Image Generation', ai: true, traditional: false },
                { feature: isRTL ? 'ترجمة القائمة تلقائياً' : 'AI-Powered Menu Translation', ai: true, traditional: false },
                { feature: isRTL ? 'منشئ اللافتات الرقمية' : 'Digital Signage Builder', ai: true, traditional: false },
                { feature: isRTL ? 'قوائم QR مجانية' : 'Free QR Code Menus', ai: true, traditional: false },
                { feature: isRTL ? 'منشئ محتوى وسائل التواصل' : 'Social Media Content Generator', ai: true, traditional: false },
                { feature: isRTL ? 'مزامنة المخزون متعدد الفروع' : 'Multi-Branch Inventory Sync', ai: true, traditional: false },
                { feature: isRTL ? 'إدارة المستودع المركزي' : 'Central Warehouse Management', ai: true, traditional: false },
                { feature: isRTL ? 'تحليلات في الوقت الفعلي' : 'Real-Time Analytics', ai: true, traditional: true },
              ].map((row, i) => (
                <div 
                  key={i} 
                  className={`grid grid-cols-3 border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors ${
                    i % 2 === 0 ? 'bg-gray-800/20' : ''
                  }`}
                >
                  <div className="p-5 text-gray-300 font-medium">{row.feature}</div>
                  <div className="p-5 flex justify-center">
                    {row.ai ? (
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <X className="w-6 h-6 text-red-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-5 flex justify-center">
                    {row.traditional ? (
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <X className="w-6 h-6 text-red-400" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-10 text-center">
              <button
                onClick={() => navigate('/demo')}
                className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl font-bold text-lg hover:opacity-90 transition-all transform hover:scale-105 inline-flex items-center gap-2"
              >
                {isRTL ? 'جرب الذكاء الاصطناعي مجاناً' : 'Try AI Features Free'}
                <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </section>

      

      {/* Pricing Section */}
      <section id="pricing-section" className="relative py-32 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <Badge className="mb-6 bg-gradient-to-r from-yellow-500 to-purple-500 text-gray-900 px-6 py-2 text-base font-bold">✨ {t('landing.pricingBadge')}</Badge>
            <h2 className="text-5xl sm:text-6xl font-black text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }} dir={isRTL ? 'rtl' : 'ltr'}>
              {t('landing.pricingTitle')}
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-teal-400">
                {t('landing.pricingSubtitle')}
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
              {t('landing.pricingDesc')}
            </p>
            
            {/* Currency Info & Selector */}
            <div className="flex items-center justify-center gap-4 mt-6" dir={isRTL ? 'rtl' : 'ltr'}>
              <p className="text-gray-400 text-sm">
                {t('landing.pricesIn')} <span className="font-semibold text-white">{currency.name}</span>
              </p>
              <CurrencySelector variant="ghost" showLabel={true} />
            </div>
          </div>
          
          {/* Billing Period Toggle */}
          <div className="flex flex-col items-center justify-center gap-4 mb-12">
            <div className="flex items-center gap-4">
              <span className={`text-lg font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-white' : 'text-gray-400'}`}>{t('landing.monthly')}</span>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                className={`relative w-16 h-8 rounded-full transition-colors duration-300 ${
                  billingPeriod === 'annual' ? 'bg-gradient-to-r from-purple-600 to-teal-600' : 'bg-gray-700'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 shadow-lg ${
                    billingPeriod === 'annual' ? 'translate-x-8' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-lg font-medium transition-colors ${billingPeriod === 'annual' ? 'text-white' : 'text-gray-400'}`}>
                {t('landing.annual')}
              </span>
            </div>
            {billingPeriod === 'annual' && (
              <Badge className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-4 py-1 animate-pulse">
                💰 {t('landing.saveAnnual')}
              </Badge>
            )}
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 max-w-7xl mx-auto">
            {[
              {
                name: 'Free',
                monthlyPriceUSD: 0,
                annualPriceUSD: 0,
                monthlySavings: 0,
                badge: 'FREE FOREVER',
                accent: '#10B981',
                features: [
                  'Core POS (1 terminal)',
                  '50 orders / month',
                  '30 menu items',
                  'Basic reports (7 days)',
                  'Table & takeaway mgmt',
                  'Powered-by watermark',
                ],
                highlight: false,
                buttonText: 'Start Free',
                
                planKey: 'free',
              },
              {
                name: 'Core POS',
                monthlyPriceUSD: 29,
                annualPriceUSD: 278,
                monthlySavings: 70,
                badge: 'CHEAPEST POS',
                accent: '#06B6D4',
                features: [
                  'Everything in Free — no caps',
                  'Unlimited orders & items',
                  'Kitchen Display System (KDS)',
                  'Advanced reports & exports',
                  '3 user accounts',
                  'No watermark · Chat support',
                ],
                highlight: false,
                buttonText: 'Subscribe Now',
                
                planKey: 'core_pos',
              },
              {
                name: 'Starter',
                monthlyPriceUSD: 79,
                annualPriceUSD: 758,
                monthlySavings: 190,
                badge: 'BASE POS',
                accent: '#94A3B8',
                features: [
                  'Full Base POS (10 features)',
                  'POS Terminal & Cashier Tablet',
                  'Menu Mgmt with AI descriptions',
                  'Inventory & Recipe Management',
                  'Kitchen Display System (KDS)',
                  'Sales Reports & Analytics',
                  'Multi-User Staff Accounts',
                ],
                highlight: false,
                buttonText: 'Subscribe Now',
                
                planKey: 'starter',
              },
              {
                name: 'Growth',
                monthlyPriceUSD: 99,
                annualPriceUSD: 950,
                monthlySavings: 238,
                badge: 'POS + ESSENTIALS',
                accent: '#60A5FA',
                features: [
                  'Full Base POS (10 features)',
                  '+ Digital Signage ($19 value)',
                  '+ Online Ordering ($19 value)',
                  'TV menu boards with USB export',
                  'Customer ordering portal & QR',
                ],
                highlight: false,
                buttonText: 'Subscribe Now',
                savings: 18,
                
              },
              {
                name: 'Pro',
                monthlyPriceUSD: 149,
                annualPriceUSD: 1430,
                monthlySavings: 358,
                badge: 'MOST POPULAR',
                accent: '#C084FC',
                features: [
                  'Everything in Growth',
                  '+ AI Content Studio ($39 value)',
                  '+ Loyalty Program ($14 value)',
                  '9 AI food-photo styles',
                  'VIP tiers & birthday auto-rewards',
                  'Social publishing (FB, IG)',
                ],
                highlight: true,
                buttonText: 'Subscribe Now',
                savings: 41,
                
              },
              {
                name: 'Enterprise',
                monthlyPriceUSD: 249,
                annualPriceUSD: 2390,
                monthlySavings: 598,
                badge: 'EVERYTHING UNLOCKED',
                accent: '#FBBF24',
                features: [
                  'Everything in Pro',
                  '+ AI Voice Ordering ($49 value)',
                  '+ Reservations ($29 value)',
                  '+ Events & Dining ($19 value)',
                  '+ Bar & Tab Management ($24 value)',
                  '+ Payment Processing ($9 value)',
                  '+ Delivery Platforms ($19 value)',
                  'Multi-location + Priority Support',
                ],
                highlight: false,
                buttonText: 'Subscribe Now',
                savings: 90,
                
              },
            ].map((plan, index) => (
              <div
                key={index}
                className={`relative p-7 rounded-3xl backdrop-blur-sm transition-all duration-500 hover:translate-y-[-4px] flex flex-col ${
                  plan.highlight
                    ? 'bg-gray-900/80 border-2 scale-[1.02] shadow-2xl'
                    : 'bg-gray-900/50 border border-gray-800 hover:border-gray-700'
                }`}
                style={plan.highlight ? {
                  borderColor: plan.accent,
                  boxShadow: `0 0 0 1px ${plan.accent}40, 0 20px 40px -20px ${plan.accent}50`,
                } : { borderColor: `${plan.accent}30` }}
              >
                {/* Badge */}
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase shadow-lg"
                  style={{
                    backgroundColor: plan.highlight ? plan.accent : `${plan.accent}20`,
                    color: plan.highlight ? '#0a0a0a' : plan.accent,
                    border: plan.highlight ? 'none' : `1px solid ${plan.accent}50`,
                  }}>
                  {plan.badge}
                </div>

                <h3 className="text-2xl font-bold text-white mb-2 mt-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {plan.name}
                </h3>

                <div className="mb-5">
                  {plan.monthlyPriceUSD === 0 ? (
                    <>
                      <span className="text-5xl font-black text-white">
                        $0
                      </span>
                      <span className="text-gray-400 ml-2 text-sm">/forever</span>
                      <div className="text-xs font-semibold mt-2" style={{ color: '#10B981' }}>
                        No credit card required
                      </div>
                    </>
                  ) : billingPeriod === 'monthly' ? (
                    <>
                      <span className="text-5xl font-black text-white">
                        {formatPrice(plan.monthlyPriceUSD).split('.')[0]}
                      </span>
                      <span className="text-gray-400 ml-2 text-sm">/month</span>
                    </>
                  ) : (
                    <>
                      <span className="text-5xl font-black text-white">
                        {formatPrice(Math.round(plan.annualPriceUSD / 12)).split('.')[0]}
                      </span>
                      <span className="text-gray-400 ml-2 text-sm">/mo</span>
                      <div className="text-xs text-gray-400 mt-2">
                        billed annually · {formatPrice(plan.annualPriceUSD).split('.')[0]}/yr
                      </div>
                      <div className="text-xs font-semibold mt-1" style={{ color: '#10B981' }}>
                        Save {formatPrice(plan.monthlySavings)}/yr
                      </div>
                    </>
                  )}
                  {currency.code !== 'USD' && (
                    <div className="text-xs text-gray-500 mt-1">
                      ≈ ${billingPeriod === 'monthly' ? plan.monthlyPriceUSD : plan.annualPriceUSD} USD
                    </div>
                  )}
                </div>

                {plan.savings > 0 && (
                  <p className="text-xs font-medium mb-3" style={{ color: '#10B981' }}>
                    Save ${plan.savings}+/mo vs à la carte
                  </p>
                )}

                <ul className="space-y-2.5 mb-6 flex-1">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: plan.accent }} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => {
                    const planKey = plan.planKey || plan.name.toLowerCase().replace(/\s+/g, '_');
                    sessionStorage.setItem('intended_plan', planKey);
                    sessionStorage.setItem('intended_billing', billingPeriod);
                    if (user) {
                      window.location.href = '/dashboard';
                    } else {
                      // New users — go straight to signup with plan preselected
                      window.location.href = `/signup?plan=${encodeURIComponent(planKey)}`;
                    }
                  }}
                  data-testid={`pricing-cta-${plan.planKey || plan.name.toLowerCase()}`}
                  className="w-full py-4 rounded-xl font-bold transition-all duration-300 text-sm hover:opacity-90"
                  style={plan.highlight ? {
                    backgroundColor: plan.accent,
                    color: '#0a0a0a',
                  } : {
                    backgroundColor: `${plan.accent}1A`,
                    color: plan.accent,
                    border: `1px solid ${plan.accent}50`,
                  }}
                >
                  {plan.buttonText}
                </button>

                {plan.monthlyPriceUSD === 0 ? (
                  <p className="text-[11px] text-gray-500 mt-2 text-center">
                    No card · upgrade anytime
                  </p>
                ) : (
                  <p className="text-[11px] text-gray-500 mt-2 text-center">
                    Cancel anytime · Prorated changes
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Power Add-ons */}
          <div className="max-w-7xl mx-auto mt-16">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                10 Premium Modules
              </h3>
              <p className="text-gray-400">Mix and match à la carte on any package. Add or remove anytime.</p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { name: 'AI Content Studio', price: 39, annualPrice: 374, desc: '9 AI photography styles', icon: 'sparkles', accent: '#C084FC' },
                { name: 'Digital Signage', price: 19, annualPrice: 182, desc: 'TV menu boards + USB export', icon: 'tv', accent: '#FBBF24' },
                { name: 'Online Ordering', price: 19, annualPrice: 182, desc: 'Customer ordering portal', icon: 'globe', accent: '#60A5FA' },
                { name: 'Loyalty Program', price: 14, annualPrice: 134, desc: 'VIP tiers · 6 months free', icon: 'heart', accent: '#F472B6', free: true },
                { name: 'Payment Processing', price: 9, annualPrice: 86, desc: 'Card terminal integration', icon: 'card', accent: '#34D399' },
                { name: 'Delivery Platforms', price: 19, annualPrice: 182, desc: 'UberEats, DoorDash sync', icon: 'truck', accent: '#A78BFA' },
                { name: 'AI Voice Ordering', price: 49, annualPrice: 470, desc: 'AI receptionist avatar', icon: 'mic', accent: '#C4B5FD' },
                { name: 'Table Reservations', price: 29, annualPrice: 278, desc: 'Online booking + waitlist', icon: 'calendar', accent: '#67E8F9' },
                { name: 'Events & Private Dining', price: 19, annualPrice: 182, desc: 'Event calendar + RSVP', icon: 'party', accent: '#F9A8D4' },
                { name: 'Bar & Tab Management', price: 24, annualPrice: 230, desc: 'Open tabs, split bills', icon: 'wine', accent: '#FCD34D' },
              ].map((addon, i) => (
                <div key={i} className="bg-gray-900/60 border rounded-xl p-4 hover:translate-y-[-2px] transition-all relative overflow-hidden"
                  style={{ borderColor: `${addon.accent}30` }}>
                  <div className="absolute -top-8 -right-8 w-20 h-20 rounded-full pointer-events-none" style={{
                    background: `radial-gradient(circle, ${addon.accent}18 0%, transparent 65%)`,
                  }} />
                  <div className="relative">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{
                      backgroundColor: `${addon.accent}18`,
                      border: `1px solid ${addon.accent}30`,
                    }}>
                      <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: addon.accent }} />
                    </div>
                    <h4 className="text-white font-semibold text-sm mb-1">{addon.name}</h4>
                    <p className="text-gray-500 text-[11px] mb-3 leading-snug">{addon.desc}</p>
                    <p className="text-white font-bold text-sm">
                      {formatPrice(billingPeriod === 'annual' ? Math.round(addon.annualPrice / 12) : addon.price).split('.')[0]}
                      <span className="text-gray-500 text-[10px] font-normal">/mo</span>
                    </p>
                    {addon.free && <p className="text-[10px] mt-1" style={{ color: '#10B981' }}>6 months free</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Compare Plans Button */}
          <div className="text-center mt-12">
            <button
              onClick={() => setShowComparison(true)}
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              <Info className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('landing.comparePlans')}
            </button>
            <p className="text-gray-400 text-sm mt-3" dir={isRTL ? 'rtl' : 'ltr'}>See complete feature comparison and scheduling differences</p>
          </div>
        </div>
      </section>

      {/* Blog Preview Strip */}
      <section className="relative py-16 bg-black border-t border-gray-800" data-testid="landing-blog-preview">
        <div className="container mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <Badge className="mb-3 bg-purple-600/20 text-purple-300 border border-purple-500/30 px-3 py-1">From the blog</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Latest POS insights</h2>
              <p className="text-gray-400 mt-2">Practical guides on restaurant tech, payments, and compliance.</p>
            </div>
            <Link to="/blog" className="hidden md:inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm font-semibold">
              View all articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { slug: 'toast-pos-alternatives-2026', title: 'Toast POS Alternatives 2026 — 8 Best Options', excerpt: '$69+/mo and locked payments push restaurants to switch. Here are the 8 best alternatives ranked by total cost.', readingMinutes: 11, heroImage: 'https://images.unsplash.com/photo-1559925393-8be0ec4767c8?w=800&h=450&fit=crop&auto=format&q=75' },
              { slug: 'restaurant-pos-system-cost', title: 'How Much Does a Restaurant POS Cost in 2026?', excerpt: 'Full breakdown of software, hardware, setup, processing, and hidden fees across every major POS platform.', readingMinutes: 9, heroImage: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=450&fit=crop&auto=format&q=75' },
              { slug: 'free-pos-food-trucks', title: 'Best Free POS App for Food Trucks (2026)', excerpt: 'We tested every free POS. Here are the 5 best for mobile-first, offline-capable, low-cost operations.', readingMinutes: 7, heroImage: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=800&h=450&fit=crop&auto=format&q=75' },
            ].map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="group block overflow-hidden rounded-2xl border border-gray-800 bg-gray-900/50 transition hover:border-purple-500/50 hover:bg-gray-900"
                data-testid={`blog-preview-${a.slug}`}
              >
                <div className="relative h-40 overflow-hidden bg-gradient-to-br from-purple-900/50 to-black">
                  <img
                    src={a.heroImage}
                    alt={a.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <h3 className="text-base font-bold text-white leading-tight drop-shadow-lg group-hover:text-purple-200">{a.title}</h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-gray-400 line-clamp-3">{a.excerpt}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                    <span>{a.readingMinutes} min read</span>
                    <span className="inline-flex items-center gap-1 text-purple-400 group-hover:gap-2 transition-all">Read <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center md:hidden">
            <Link to="/blog" className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm font-semibold">
              View all articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Dedicated FAQ Section (SEO — rich snippet eligible) */}
      <section id="faq" className="relative py-20 bg-gradient-to-b from-black via-gray-950 to-black border-t border-gray-800" data-testid="landing-faq-section">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-purple-600/30 to-pink-600/30 text-purple-200 border border-purple-500/40 px-4 py-1.5">Frequently asked questions</Badge>
            <h2 className="text-4xl sm:text-5xl font-black text-white" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}>
              Questions? We've got answers.
            </h2>
            <p className="text-gray-400 mt-3 text-lg">Everything you need to know before signing up — and yes, the free plan is really free.</p>
          </div>
          <div className="space-y-3">
            {[
              { q: 'Is FlavorFeed really free?', a: 'Yes. Free Forever = $0/month, no credit card required. Core POS on 1 terminal, 50 orders/month, 30 menu items, basic reports. A small "Powered by FlavorFeed" line appears on receipts. Upgrade to Core POS at $29/mo to remove limits.' },
              { q: "What's the catch with the free POS?", a: 'No catch. We make money when you grow — free users who exceed 50 orders/month tend to upgrade to Core POS ($29), and bigger restaurants add modules like AI Content Studio ($39) or Loyalty ($14). The free tier is a genuine product, not a 14-day trial in disguise.' },
              { q: 'Can I pay only for the modules I actually use?', a: 'Yes — that\'s the whole point of our modular pricing. Start with Core POS ($29). Then toggle on only what you need: Digital Signage ($19), Online Ordering ($19), Loyalty ($14), AI Content Studio ($39), Payment Processing ($9), Delivery Platforms ($19), Table Reservations ($29), AI Voice Ordering ($49), Events ($19), Bar & Tabs ($24), AI Invoice Scanner ($19). No "you must buy the $249 plan for one feature" nonsense.' },
              { q: 'Can I upgrade or downgrade anytime?', a: 'Yes — any tier or module change takes effect immediately and is prorated to the day. No contracts, no exit fees, your data is always exportable to CSV.' },
              { q: 'What hardware do I need to run FlavorFeed?', a: 'Any iPad, Android tablet, Chromebook, or even your iPhone. For Apple Pay / Google Pay acceptance, use Tap-to-Pay on iPhone XS or newer — zero extra hardware. For full-service restaurants we recommend Sunmi V2 Pro terminals ($330) or Stripe Reader M2 ($59).' },
              { q: 'Is FlavorFeed ZATCA Phase 2 compliant for Saudi Arabia?', a: 'FlavorFeed is built to the ZATCA Phase 2 (Fatoora) specification — UBL 2.1 XML e-invoices, cryptographic stamping, QR codes, and bilingual Arabic/English receipts, with automatic submission to the Fatoora portal.' },
              { q: 'Does FlavorFeed work offline?', a: 'Yes. If WiFi drops, orders queue locally and sync when connection returns. Cash payments work normally. Card transactions pre-authorize and complete on reconnect. Critical for food trucks and festivals.' },
              { q: 'How does FlavorFeed compare to Toast, Square, Clover, and Lightspeed?', a: 'Starts free vs Toast at $69+/mo. Includes KDS, loyalty, and online ordering at no extra cost vs Square/Toast charging $25–75/mo each. Bring-your-own payment processor (Stripe, Helcim, Valor) vs Clover locking you into Fiserv. See our detailed comparisons at /vs/toast, /vs/square, /vs/clover, /vs/lightspeed.' },
              { q: 'Do you support multi-branch restaurants?', a: 'Yes. Enterprise tier includes our Multi-Branch HQ Control Tower — one account manages all branches with strict data isolation, branch-specific menus/pricing, consolidated reports, and central warehouse/transfer workflows.' },
              { q: 'Can I import my existing menu and customers?', a: 'Yes — we import menu, customer lists, and historical orders from CSV exports from Toast, Square, Clover, Lightspeed, and most POS systems. Free and typically completes in under 48 hours.' },
            ].map((item, i) => (
              <details key={i} className="group bg-gray-900/60 backdrop-blur-sm border border-gray-800 hover:border-purple-500/40 rounded-2xl overflow-hidden transition-colors" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer list-none p-6 font-semibold text-white flex items-center justify-between gap-6">
                  <span className="text-base sm:text-lg">{item.q}</span>
                  <span className="flex-shrink-0 w-8 h-8 rounded-full border border-purple-500/40 bg-purple-500/10 flex items-center justify-center text-purple-300 text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <div className="px-6 pb-6 text-gray-300 leading-relaxed text-sm sm:text-base">{item.a}</div>
              </details>
            ))}
          </div>
          <div className="mt-12 text-center rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-black p-8">
            <h3 className="text-2xl font-bold text-white">Still have questions?</h3>
            <p className="text-gray-400 mt-2">Start the free plan and test everything yourself. No credit card, no time limit.</p>
            <button
              onClick={() => { sessionStorage.setItem('intended_plan', 'free'); window.location.href = user ? '/dashboard' : '/signup?plan=free'; }}
              className="mt-5 inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3.5 rounded-xl font-bold hover:opacity-90 transition"
              data-testid="faq-cta-start-free"
            >
              Start Free · No Card Needed <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Restaurant Management CTA - Compact */}
      <section className="relative py-16 bg-gradient-to-b from-black via-violet-950/10 to-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="relative overflow-hidden bg-gradient-to-br from-purple-900/30 to-black border border-purple-500/30 hover:border-purple-500/50 transition-all">
              <div className="relative p-8 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
                <Badge className="mb-4 bg-gradient-to-r from-purple-500 to-red-500 text-white px-4 py-1.5 text-sm font-bold">
                  🍽️ {t('landing.restaurantMgmtBadge')}
                </Badge>
                
                <h3 className="text-2xl md:text-3xl font-bold mb-3 text-white">
                  {t('landing.restaurantMgmtTitle')}
                </h3>
                
                <p className="text-base text-gray-300 mb-6 max-w-2xl mx-auto">
                  {t('landing.restaurantMgmtDesc')}
                </p>

                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  {[
                    { icon: <ShoppingCart className="w-4 h-4" />, label: t('landing.restaurantMgmtPOS') },
                    { icon: <Heart className="w-4 h-4" />, label: t('landing.restaurantMgmtLoyalty') },
                    { icon: <UserCheck className="w-4 h-4" />, label: t('landing.restaurantMgmtHR') },
                    { icon: <DollarSign className="w-4 h-4" />, label: t('landing.restaurantMgmtAccounting') }
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 px-3 py-1.5 bg-black/40 rounded-lg border border-purple-500/20">
                      <div className="text-purple-400">
                        {feature.icon}
                      </div>
                      <span className="text-xs font-semibold text-white">{feature.label}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => window.location.href = '/pos'}
                  className="bg-gradient-to-r from-purple-600 to-red-600 hover:from-purple-700 hover:to-red-700 text-white px-8 py-3 text-base rounded-full shadow-lg hover:shadow-purple-500/30 transition-all"
                >
                  {t('landing.explorePOS')}
                  <ArrowRight className={`w-4 h-4 inline ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Plan Comparison Modal */}
      <PlanComparisonModal 
        isOpen={showComparison} 
        onClose={() => setShowComparison(false)} 
      />

      {/* Final CTA - Bold & Impactful */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-teal-900 to-coral-900"></div>
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(to right, #8B5CF6 1px, transparent 1px), linear-gradient(to bottom, #8B5CF6 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        </div>

        <div className="container mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-8 text-white leading-tight text-center" style={{ fontFamily: isRTL ? 'Almarai, sans-serif' : 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }} dir={isRTL ? 'rtl' : 'ltr'}>
            {t('landing.readyToGo')}
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-coral-400 to-purple-400 animate-gradient">
              {t('landing.viral')}
            </span>
          </h2>
          <p className="text-2xl text-gray-300 mb-12 max-w-3xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
            {t('landing.joinRestaurants')}
          </p>
          <button
            data-testid="final-cta-btn"
            onClick={() => user ? window.location.href = '/dashboard' : window.location.href = '/login'}
            size="lg"
            className="group bg-white text-black hover:bg-gray-100 px-12 py-8 text-2xl rounded-full shadow-2xl hover:shadow-white/20 transform hover:scale-110 transition-all duration-300 font-black"
            style={{ fontFamily: isRTL ? 'Almarai, sans-serif' : 'Space Grotesk, sans-serif' }}
          >
            {user ? t('landing.goToDashboard') : t('landing.startCreatingNow')}
            <ArrowRight className="inline-block w-8 h-8 ml-3 group-hover:translate-x-2 transition-transform" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-12">
        <div className="container mx-auto px-6">
          <div className="text-center mb-6">
            <img 
              src="/flavorfeed-logo.png" 
              alt="FlavorFeed AI - Culinary Intelligence" 
              className="h-20 w-auto mx-auto mb-4 object-contain"
            />
          </div>
          
          {/* Footer Links */}
          <div className="flex items-center justify-center gap-6 mb-4" dir={isRTL ? 'rtl' : 'ltr'}>
            <a 
              href="/privacy-policy" 
              className="text-gray-400 hover:text-purple-400 transition-colors text-sm font-medium"
            >
              {t('landing.privacyPolicy')}
            </a>
            <span className="text-gray-700">•</span>
            <a 
              href="/terms-of-service" 
              className="text-gray-400 hover:text-purple-400 transition-colors text-sm font-medium"
            >
              {t('landing.termsOfService')}
            </a>
            <span className="text-gray-700">•</span>
            <a 
              href="mailto:feed@flavorfeed.ai" 
              className="text-gray-400 hover:text-purple-400 transition-colors text-sm font-medium"
            >
              {t('landing.contactUs')}
            </a>
          </div>
          
          <p className="text-gray-500 text-sm text-center" dir={isRTL ? 'rtl' : 'ltr'}>
            © 2025 {t('landing.footerCopyright')}
          </p>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      {/* Plan Comparison Modal */}
      {showComparison && <PlanComparisonModal onClose={() => setShowComparison(false)} />}

      {/* Video Demo Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowVideoModal(false)}
        >
          <div className="relative w-full max-w-5xl mx-4 animate-scale-in">
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors flex items-center gap-2 text-sm"
            >
              <span>Close</span>
              <X className="w-6 h-6" />
            </button>
            
            {/* Video Container */}
            <div 
              className="relative rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-500/30"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Glowing Border Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-teal-500 to-coral-500 rounded-2xl blur opacity-30 animate-pulse"></div>
              
              <div className="relative bg-gray-900 rounded-2xl overflow-hidden">
                <video
                  autoPlay
                  controls
                  className="w-full aspect-video"
                  poster="/flavorfeed-logo.png"
                >
                  <source src={PROMO_VIDEO_URL} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
            
            {/* Video Title */}
            <div className="text-center mt-6">
              <h3 className="text-2xl font-bold text-white mb-2">FlavorFeed AI - All-in-One Platform</h3>
              <p className="text-gray-400">One Platform. Every Workflow. Infinite Possibilities.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}